import type { ScreenRect } from './layout.js';
import { normalizedToScreen } from './layout.js';
import type { OverlayPlan } from './overlayPlan.js';

/**
 * Marker layer: the readable part of the overlay.
 *
 * Labels and problem pins are HTML (not SVG) so they stay crisp at any display
 * size and remain touch targets. Positions are derived from the same normalized
 * coordinates as the SVG, through the same layout rectangle, so they cannot
 * drift from the photo.
 */

export interface MarkerRenderInput {
  plan: OverlayPlan;
  /** Base rect of the photo, relative to the marker layer (no user transform). */
  base: ScreenRect;
  /** Marker layer size in CSS pixels, used to cull off-screen labels. */
  layerSize: { width: number; height: number };
  onSelect?(measurementId: string): void;
}

const iconFor = (status: string): string => {
  switch (status) {
    case 'pass':
      return '✓';
    case 'fail':
      return '✕';
    case 'warning':
      return '!';
    case 'uncertain':
      return '?';
    default:
      return '•';
  }
};

export const renderMarkers = (container: HTMLElement, input: MarkerRenderInput): void => {
  const nodes: HTMLElement[] = [];
  const { base, layerSize } = input;

  const isVisible = (x: number, y: number): boolean =>
    x >= -8 && x <= layerSize.width + 8 && y >= -8 && y <= layerSize.height + 8;

  for (const label of input.plan.labels) {
    const point = normalizedToScreen(label.point, base);
    if (!isVisible(point.x, point.y)) continue;
    const element = document.createElement('button');
    element.type = 'button';
    const role = label.role ?? 'value';
    element.className = `pv-marker pv-marker--${role} pv-status-${label.status}`;
    element.style.left = `${point.x}px`;
    element.style.top = `${point.y}px`;
    element.title = label.tooltip;
    element.dataset['measurementId'] = label.id.replace(/^label:/, '');
    if (label.interactive) {
      element.addEventListener('click', (event) => {
        event.stopPropagation();
        input.onSelect?.(element.dataset['measurementId'] ?? '');
      });
    } else {
      element.style.pointerEvents = 'none';
    }
    element.append(document.createTextNode(`${iconFor(label.status)} ${label.text}`));
    nodes.push(element);
  }

  for (const marker of input.plan.problemMarkers) {
    const anchor = normalizedToScreen(
      { x: marker.rect.x + marker.rect.width, y: marker.rect.y },
      base,
    );
    if (!isVisible(anchor.x, anchor.y)) continue;
    const element = document.createElement('button');
    element.type = 'button';
    element.className = `pv-marker pv-marker--pin pv-status-${marker.severity === 'error' ? 'fail' : 'warning'}`;
    element.style.left = `${anchor.x}px`;
    element.style.top = `${anchor.y}px`;
    element.title = marker.message;
    element.dataset['problemId'] = marker.id;
    element.dataset['measurementId'] = marker.checkId;
    element.addEventListener('click', (event) => {
      event.stopPropagation();
      input.onSelect?.(marker.checkId);
    });
    element.append(document.createTextNode(marker.severity === 'error' ? '✕' : '!'));
    nodes.push(element);
  }

  container.replaceChildren(...nodes);
};

export const clearMarkers = (container: HTMLElement): void => {
  container.replaceChildren();
};
